/**
 * Single source of truth for agency details shown across the site.
 * Update these values rather than editing copy inside components.
 */
export const site = {
  name: "CatalyxLabs",
  tagline: "We design, build, and ship the software you have in your head.",
  email: "contactus@catalyxlabs.co",
  location: "Remote-first · US & EU time zones",
  responseTime: "within one business day",
  social: [
    { label: "GitHub", href: "https://github.com/catalyxlabs" },
    { label: "Instagram", href: "https://www.instagram.com/catalyx_labs/" },
  ],
} as const;

/**
 * Feature flags. Flip `portfolio` to true once there are real case studies in
 * `server/src/data/projects.ts` — that restores the /portfolio route, its nav
 * and footer links, and the "see our work" calls to action.
 */
export const features = {
  portfolio: false,
};

export interface NavLink {
  label: string;
  to: string;
}

export const navLinks: NavLink[] = [
  { label: "Home", to: "/" },
  ...(features.portfolio ? [{ label: "Work", to: "/portfolio" }] : []),
];

export interface Capability {
  id: string;
  title: string;
  description: string;
  skills: string[];
}

export const capabilities: Capability[] = [
  {
    id: "frontend",
    title: "Frontend Engineering",
    description:
      "Interfaces that stay fast and accessible as they grow. We build with React and TypeScript, and treat performance and accessibility as release requirements rather than follow-up tickets.",
    skills: ["React", "TypeScript", "Sass", "Design systems", "Accessibility", "Web performance"],
  },
  {
    id: "backend",
    title: "Backend Engineering",
    description:
      "APIs and services built to be understood by the team that inherits them. Node.js at the core, with schemas, tests, and observability wired in from the first commit.",
    skills: ["Node.js", "REST & GraphQL", "PostgreSQL", "Event pipelines", "Auth", "Integrations"],
  },
  {
    id: "fullstack",
    title: "Fullstack Product Teams",
    description:
      "A single team that owns a feature end to end — schema through interface. Fewer handoffs, shorter feedback loops, and one group accountable for the outcome.",
    skills: ["MVP delivery", "Feature ownership", "Prototyping", "Technical discovery", "Legacy rescue"],
  },
  {
    id: "infrastructure",
    title: "Infrastructure & DevOps",
    description:
      "The unglamorous layer that decides whether shipping is calm or stressful. We automate environments, pipelines, and recovery so releases stop being events.",
    skills: ["AWS / GCP / Azure", "Terraform", "Docker & Kubernetes", "CI/CD", "Monitoring", "Cost tuning"],
  },
  {
    id: "design",
    title: "Product Design",
    description:
      "Research, interaction design, and visual craft that answer a real user problem. Designers work alongside engineers throughout, so what gets drawn is what gets built.",
    skills: ["User research", "UX & IA", "UI design", "Prototyping", "Design systems", "Usability testing"],
  },
  {
    id: "delivery",
    title: "Project Management",
    description:
      "A named delivery lead who keeps scope, budget, and risk visible. You get weekly demos and a clear picture of progress instead of a status report written the night before.",
    skills: ["Delivery planning", "Agile facilitation", "Risk management", "Stakeholder comms", "QA coordination"],
  },
];

export interface ProcessStep {
  title: string;
  description: string;
}

export const processSteps: ProcessStep[] = [
  {
    title: "Discovery call",
    description:
      "A 30-minute conversation about the problem, the constraints, and what success actually looks like. No pitch deck.",
  },
  {
    title: "Scope & proposal",
    description:
      "We come back with a written plan: approach, team shape, milestones, and a fixed or capped budget you can take to finance.",
  },
  {
    title: "Build in the open",
    description:
      "Two-week iterations with a working demo at the end of each one. You see progress in the product, not in a spreadsheet.",
  },
  {
    title: "Launch & hand over",
    description:
      "We ship it, document it, and train your team. If you'd rather we keep running it, we can do that too.",
  },
];

export interface Engagement {
  title: string;
  description: string;
  bestFor: string;
}

export const engagements: Engagement[] = [
  {
    title: "Project build",
    description:
      "A defined scope with a fixed timeline and budget. We assemble the right mix of design and engineering and deliver the whole thing.",
    bestFor: "New products, replatforms, and launches with a firm deadline.",
  },
  {
    title: "Embedded team",
    description:
      "Our engineers, designers, and delivery lead join your existing team and work inside your process and tooling.",
    bestFor: "In-house teams that need to add capacity or a missing specialty.",
  },
  {
    title: "Technical partnership",
    description:
      "Ongoing engineering support — roadmap input, maintenance, infrastructure, and a team that already knows your codebase.",
    bestFor: "Companies without an in-house engineering function.",
  },
];

export const stats = [
  { value: "6", label: "Disciplines in-house" },
  { value: "2 wks", label: "Typical start time" },
  { value: "1 day", label: "Response time" },
] as const;

export const BUDGET_LABELS: Record<string, string> = {
  "under-25k": "Under $25,000",
  "25k-75k": "$25,000 – $75,000",
  "75k-150k": "$75,000 – $150,000",
  "150k-plus": "$150,000+",
  "not-sure": "Not sure yet",
};

export const TIMELINE_LABELS: Record<string, string> = {
  asap: "As soon as possible",
  "1-3-months": "In the next 1–3 months",
  "3-6-months": "In the next 3–6 months",
  exploring: "Just exploring for now",
};
